
import { snakeKeysDeep } from './_caseTransform';
import { absoluteApiBaseUrl } from './absoluteUrl';

interface ApiConfig {
  baseUrl: string;
  timeout: number;
  enableMockFallback: boolean;
  demoMode: boolean;
}

interface ApiResponse<T> {
  data: T;
  success: boolean;
  source: 'api' | 'mock';
  error?: string;
}

/**
 * Async function returning the current bearer token (typically a Clerk JWT)
 * or null when the user is signed out. Resolves on each request so token
 * rotation by the auth provider is picked up without restarting the client.
 */
type TokenFetcher = () => Promise<string | null | undefined>;

/** Longest a request waits for Clerk to decide signed in / out before going out anonymous (a Clerk
 *  outage must not hang the page). Normally the wait is however long Clerk takes to load. */
const AUTH_SETTLE_MAX_MS = 20_000;


/**
 * A FastAPI error `detail` as text. A request-validation 422 carries a list of
 * `{ loc, msg, type }` objects; show their messages ("This name contains a word we don't
 * allow…"), not the JSON. Anything else that isn't a string stays JSON, as before.
 */
export function formatErrorDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0
      && detail.every((item) => typeof (item as { msg?: unknown })?.msg === 'string')) {
    return detail.map((item) => (item as { msg: string }).msg.replace(/^Value error, /, '')).join('; ');
  }
  return JSON.stringify(detail);
}

class ApiClient {
  private config: ApiConfig;
  private tokenFetcher: TokenFetcher | null = null;
  // Whether Clerk has decided (signed in → a fetcher, signed out → none). Requests made before
  // that wait for it rather than going out without a token and getting 401s.
  private authSettled = false;
  private authSettledWaiters: Array<() => void> = [];

  constructor(config: ApiConfig) {
    this.config = config;
  }

  /**
   * Called by the auth bridge once Clerk has loaded: the session's token fetcher, or null when
   * signed out. Releases every request waiting for it.
   */
  setAuthState(fetcher: TokenFetcher | null) {
    this.tokenFetcher = fetcher;
    this.authSettled = true;
    const waiters = this.authSettledWaiters;
    this.authSettledWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  /** Resolves once Clerk has decided signed in / out, or after AUTH_SETTLE_MAX_MS. */
  private waitForAuthSettled(): Promise<void> {
    if (this.authSettled) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.authSettledWaiters = this.authSettledWaiters.filter((w) => w !== done);
        resolve();
      }, AUTH_SETTLE_MAX_MS);
      const done = () => { clearTimeout(timer); resolve(); };
      this.authSettledWaiters.push(done);
    });
  }

  /**
   * Inject the auth-token-fetcher from the React side once Clerk is mounted.
   * The bridge component (App.tsx) calls this with Clerk's `getToken` so
   * every authenticated request picks up the current session JWT. Pass null
   * to revert to anonymous requests (e.g. on sign-out).
   */
  setTokenFetcher(fetcher: TokenFetcher | null) {
    this.tokenFetcher = fetcher;
  }

  /** The API base URL this client talks to — relative (`/api`) when the portal and API share a host. */
  getBaseUrl(): string {
    return this.config.baseUrl;
  }

  /** The API base URL as an absolute URL, for commands copied out of the browser ("Copy as curl"). */
  getAbsoluteBaseUrl(): string {
    return absoluteApiBaseUrl(this.config.baseUrl, window.location.origin);
  }

  /** A fresh session token, or null when signed out. Clerk session JWTs expire in ~60s. */
  async getAuthToken(): Promise<string | null> {
    await this.waitForAuthSettled();
    try {
      return this.tokenFetcher ? await this.tokenFetcher() : null;
    } catch {
      return null;
    }
  }

  async get<T>(
    endpoint: string,
    mockData?: T,
    opts?: { timeoutMs?: number },
  ): Promise<ApiResponse<T>> {
    if (this.config.demoMode) {
      console.log(`[API] Demo mode: returning mock data for ${endpoint}`);
      return this.createMockResponse(mockData);
    }

    try {
      const response = await this.makeRequest<T>('GET', endpoint, undefined, opts);
      return {
        data: response,
        success: true,
        source: 'api'
      };
    } catch (error) {
      console.warn(`[API] Request failed for ${endpoint}:`, error);
      
      if (this.config.enableMockFallback && mockData !== undefined) {
        console.log(`[API] Falling back to mock data for ${endpoint}`);
        return this.createMockResponse(mockData);
      }

      throw error;
    }
  }

  /**
   * Fetch EVERY row of a paged list endpoint.
   *
   * The API caps a list page at 100 rows (`limit` ≤ 100, newest first) and a
   * plain `get()` only ever sees page one. Once a table outgrows the cap the
   * oldest rows silently vanish from the UI — Summation's mounted catalogs
   * rendered as bare UUIDs and the fanatics clusters dropped off Compute the
   * moment those tables passed 100 rows. Every list a page or picker renders
   * in full goes through here so the row count never matters to the user.
   *
   * Walks `offset`/`limit` until a short page comes back (the API orders every
   * list deterministically — `stable_order` — so pages partition the set).
   * Query strings already on `endpoint` are preserved. In demo mode, or when
   * the first request fails and mock fallback is on, the mock list is returned
   * as-is (it is already the complete set).
   */
  async getAll<T>(
    endpoint: string,
    mockData?: T[],
    opts?: { timeoutMs?: number; pageSize?: number },
  ): Promise<ApiResponse<T[]>> {
    const pageSize = Math.min(Math.max(opts?.pageSize ?? 100, 1), 100);
    const sep = endpoint.includes('?') ? '&' : '?';
    // Hard stop so a misbehaving endpoint (e.g. one that ignores `offset`)
    // can never spin the browser forever: 200 pages = 20k rows.
    const maxPages = 200;
    const all: T[] = [];
    for (let page = 0; page < maxPages; page++) {
      const offset = page * pageSize;
      const resp = await this.get<T[]>(
        `${endpoint}${sep}offset=${offset}&limit=${pageSize}`,
        page === 0 ? mockData : undefined,
        opts,
      );
      if (resp.source === 'mock') return resp;
      const rows = Array.isArray(resp.data) ? resp.data : [];
      all.push(...rows);
      if (rows.length < pageSize) {
        return { data: all, success: true, source: 'api' };
      }
    }
    console.warn(`[API] getAll stopped after ${maxPages} pages for ${endpoint}`);
    return { data: all, success: true, source: 'api' };
  }

  async post<T>(
    endpoint: string,
    data: unknown,
    mockResponse?: T,
    opts?: { timeoutMs?: number },
  ): Promise<ApiResponse<T>> {
    if (this.config.demoMode) {
      console.log(`[API] Demo mode: returning mock response for POST ${endpoint}`);
      return this.createMockResponse(mockResponse);
    }

    try {
      const response = await this.makeRequest<T>('POST', endpoint, data, opts);
      return {
        data: response,
        success: true,
        source: 'api'
      };
    } catch (error) {
      console.warn(`[API] POST request failed for ${endpoint}:`, error);
      
      if (this.config.enableMockFallback && mockResponse !== undefined) {
        console.log(`[API] Falling back to mock response for POST ${endpoint}`);
        return this.createMockResponse(mockResponse);
      }

      throw error;
    }
  }

  async put<T>(endpoint: string, data: unknown, mockResponse?: T): Promise<ApiResponse<T>> {
    if (this.config.demoMode) {
      console.log(`[API] Demo mode: returning mock response for PUT ${endpoint}`);
      return this.createMockResponse(mockResponse);
    }

    try {
      const response = await this.makeRequest<T>('PUT', endpoint, data);
      return {
        data: response,
        success: true,
        source: 'api'
      };
    } catch (error) {
      console.warn(`[API] PUT request failed for ${endpoint}:`, error);
      
      if (this.config.enableMockFallback && mockResponse !== undefined) {
        console.log(`[API] Falling back to mock response for PUT ${endpoint}`);
        return this.createMockResponse(mockResponse);
      }

      throw error;
    }
  }

  async delete<T>(endpoint: string, mockResponse?: T, opts?: { timeoutMs?: number }): Promise<ApiResponse<T>> {
    if (this.config.demoMode) {
      console.log(`[API] Demo mode: returning mock response for DELETE ${endpoint}`);
      return this.createMockResponse(mockResponse);
    }

    try {
      const response = await this.makeRequest<T>('DELETE', endpoint, undefined, opts);
      return {
        data: response,
        success: true,
        source: 'api'
      };
    } catch (error) {
      console.warn(`[API] DELETE request failed for ${endpoint}:`, error);
      
      if (this.config.enableMockFallback && mockResponse !== undefined) {
        console.log(`[API] Falling back to mock response for DELETE ${endpoint}`);
        return this.createMockResponse(mockResponse);
      }

      throw error;
    }
  }

  async patch<T>(endpoint: string, data: unknown, mockResponse?: T): Promise<ApiResponse<T>> {
    if (this.config.demoMode) {
      console.log(`[API] Demo mode: returning mock response for PATCH ${endpoint}`);
      return this.createMockResponse(mockResponse);
    }

    try {
      const response = await this.makeRequest<T>('PATCH', endpoint, data);
      return {
        data: response,
        success: true,
        source: 'api'
      };
    } catch (error) {
      console.warn(`[API] PATCH request failed for ${endpoint}:`, error);

      if (this.config.enableMockFallback && mockResponse !== undefined) {
        console.log(`[API] Falling back to mock response for PATCH ${endpoint}`);
        return this.createMockResponse(mockResponse);
      }

      throw error;
    }
  }

  /**
   * Resolve the request headers, including the bearer token. Shared by the
   * JSON request path and the streaming path.
   */
  private async buildHeaders(): Promise<Record<string, string>> {
    // Resolve the bearer token per-request so token rotation by Clerk
    // (~60s default lifetime) is picked up automatically. Tolerates a
    // missing fetcher (pre-bridge-mount) and a missing token (signed-out)
    // so anonymous endpoints like /api/health continue to work.
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    // Mount-order race: data contexts (Cloud/Organization/Project/…) issue their first fetch on
    // mount, before Clerk has loaded its session and ApiAuthBridge has handed over getToken.
    // Wait until Clerk has decided (signed in or out) so the first calls carry the token. This
    // used to poll for at most 3s; on a slow first load Clerk took longer, and the early calls
    // went out anonymous and came back 401 ("Failed to load plans", empty lists) (2026-10-08).
    await this.waitForAuthSettled();
    if (this.tokenFetcher) {
      try {
        const token = await this.tokenFetcher();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch (err) {
        console.warn('[API] Token fetch failed; sending anonymous request', err);
      }
    }
    return headers;
  }

  /** Surface the API's error detail (FastAPI `{ detail }`) as `HTTP <status>: <detail>`. */
  private async toHttpError(response: Response): Promise<Error> {
    let detail = response.statusText;
    try {
      const body = await response.json();
      if (body?.detail !== undefined && body.detail !== null) {
        detail = formatErrorDetail(body.detail);
      } else if (typeof body?.message === 'string') {
        detail = body.message;
      }
    } catch {
      /* non-JSON error body — keep the status text */
    }
    return new Error(`HTTP ${response.status}: ${detail}`);
  }

  /**
   * Open a long-lived streaming GET (Server-Sent Events / chunked text) and
   * return the raw `Response` so the caller can read `response.body`. No
   * timeout — the caller owns the lifetime via `signal`. Rejects with the
   * same `HTTP <status>: <detail>` Error shape as the JSON path. Never mocks.
   */
  async stream(endpoint: string, opts: { signal?: AbortSignal } = {}): Promise<Response> {
    const url = `${this.config.baseUrl}${endpoint}`;
    const headers = await this.buildHeaders();
    headers.Accept = 'text/event-stream';
    const response = await fetch(url, { method: 'GET', headers, signal: opts.signal });
    if (!response.ok) {
      throw await this.toHttpError(response);
    }
    return response;
  }

  /**
   * POST a JSON body and return the raw `Response` for a BINARY reply (e.g. an
   * Arrow IPC stream from the query endpoint's `results_as_arrow_ipc`). Same
   * auth, snake_casing and timeout handling as the JSON path; rejects with the
   * same `HTTP <status>: <detail>` Error shape when the API answers non-2xx
   * (errors are always JSON, so the detail is still surfaced). Never mocks.
   */
  async postBinary(
    endpoint: string,
    data: unknown,
    opts?: { timeoutMs?: number; accept?: string },
  ): Promise<Response> {
    const url = `${this.config.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), opts?.timeoutMs ?? this.config.timeout);
    const headers = await this.buildHeaders();
    if (opts?.accept) headers.Accept = opts.accept;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(snakeKeysDeep(data)),
        signal: controller.signal,
      });
      if (!response.ok) {
        throw await this.toHttpError(response);
      }
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Send a file as the raw request body (`application/octet-stream`) — the upload
   * endpoints stream it to disk server-side. XMLHttpRequest (not fetch) so the
   * caller gets upload progress. No timeout by default (a big file on a slow link
   * takes as long as it takes); cancel with `signal`. Resolves with the parsed
   * JSON reply; rejects with the same `HTTP <status>: <detail>` Error shape as
   * the JSON path. Never mocks.
   *
   * `ticketEndpoint`: POST it first and send the upload with the returned upload
   * ticket instead of the Clerk token. The ingress buffers the whole body before
   * the API sees the request, so the 60s Clerk token has expired by the time a
   * slow upload arrives (Vaden 2026-10-01: ~500 MB over 7 min → 401 after every
   * byte). The ticket outlasts the upload, and the ticket call runs the
   * upload's checks first, so a doomed upload fails before any bytes are sent.
   */
  async uploadFile<T>(
    method: 'PUT' | 'POST',
    endpoint: string,
    file: Blob,
    opts: {
      onProgress?: (loaded: number, total: number) => void;
      signal?: AbortSignal;
      ticketEndpoint?: string;
    } = {},
  ): Promise<T> {
    const url = `${this.config.baseUrl}${endpoint}`;
    let headers: Record<string, string>;
    if (opts.ticketEndpoint) {
      const ticket = await this.makeRequest<{ ticket: string; header_name: string }>(
        'POST', opts.ticketEndpoint, undefined);
      headers = { [ticket.header_name]: ticket.ticket };
    } else {
      headers = await this.buildHeaders();
    }
    return await new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url);
      for (const [name, value] of Object.entries(headers)) {
        if (name.toLowerCase() !== 'content-type') xhr.setRequestHeader(name, value);
      }
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      if (opts.onProgress) {
        xhr.upload.onprogress = (e) => opts.onProgress?.(e.loaded, e.lengthComputable ? e.total : file.size);
      }
      xhr.onload = () => {
        let body: unknown = null;
        try { body = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { /* non-JSON */ }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(body as T);
          return;
        }
        const errorDetail = (body as { detail?: unknown } | null)?.detail;
        const detail = errorDetail !== undefined && errorDetail !== null
          ? formatErrorDetail(errorDetail)
          : (xhr.status === 413 ? 'The file is too large to upload.' : xhr.statusText || 'Upload failed');
        reject(new Error(`HTTP ${xhr.status}: ${detail}`));
      };
      xhr.onerror = () => reject(new Error('Upload failed — the connection was interrupted.'));
      xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
      if (opts.signal) {
        if (opts.signal.aborted) { xhr.abort(); return; }
        opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      xhr.send(file);
    });
  }

  private async makeRequest<R>(
    method: string,
    endpoint: string,
    data?: unknown,
    opts?: { timeoutMs?: number },
  ): Promise<R> {
    const url = `${this.config.baseUrl}${endpoint}`;
    const controller = new AbortController();
    // Per-request override for calls with legitimately long round-trips
    // (e.g. /query: pod-exec for signing material + Flight SQL connect + the
    // statement itself can far exceed the 10s default).
    const timeoutId = setTimeout(() => controller.abort(), opts?.timeoutMs ?? this.config.timeout);

    const headers = await this.buildHeaders();

    try {
      const response = await fetch(url, {
        method,
        headers,
        // Services build request bodies in camelCase (matching the TS models);
        // the API expects snake_case and forbids unknown fields, so serialize
        // keys to snake_case here (free-form maps like tags are preserved).
        body: data !== undefined ? JSON.stringify(snakeKeysDeep(data)) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Surface the API's error detail (FastAPI returns { detail: ... }) rather
        // than just the status text, so callers/UI can show why it failed.
        throw await this.toHttpError(response);
      }

      // 204 No Content (e.g. ending an editor session) has no body to parse.
      if (response.status === 204) return undefined as R;
      // The response body is the endpoint's JSON; callers name its type (R).
      return (await response.json()) as R;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  private createMockResponse<T>(mockData?: T): ApiResponse<T> {
    return {
      data: mockData as T,
      success: true,
      source: 'mock'
    };
  }

  updateConfig(newConfig: Partial<ApiConfig>) {
    this.config = { ...this.config, ...newConfig };
  }
}

// Default configuration
const defaultConfig: ApiConfig = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'https://localhost:8000/api',
  timeout: 10000,
  // Opt-IN (was opt-out): mock fallback silently masks real API failures —
  // including auth failures, which made the portal look like it was showing real
  // data (e.g. "all orgs") when the API had actually errored. Now OFF unless a
  // demo explicitly sets VITE_ENABLE_MOCK_FALLBACK=true.
  enableMockFallback: import.meta.env.VITE_ENABLE_MOCK_FALLBACK === 'true',
  demoMode: import.meta.env.VITE_DEMO_MODE === 'true'
};

export const apiClient = new ApiClient(defaultConfig);
export type { ApiResponse, ApiConfig };
